import { useEffect, useState } from 'react';
// Forecasting is GLOBAL data shared across sites — spots + cached forecasts
// live in the producing project's Firestore (forecastDb; aliases the primary
// db on the chadha build). On satellite sites the browser can't write that
// project (different Auth), so tunable writes go through this site's API
// (Admin SDK → producer Firestore). Reads stay on forecastDb.
import firebase, { auth, forecastDb as db } from '../firebaseConfig';
import { DEFAULT_THRESHOLDS, fetchSpotForecast, attachTideStage, densifyForecastHours } from '../utils/forecast';
import SITE from '../site';

function prepareCachedForecast(data) {
  const hours = densifyForecastHours(data.hours || []);
  attachTideStage(hours);
  return {
    ...data,
    hours,
    rangeStart: hours[0]?.t ?? data.rangeStart,
    rangeEnd: hours[hours.length - 1]?.t ?? data.rangeEnd,
    cached: true,
  };
}

// Tracked surf spots for the Forecast page. Each doc:
//   { name, region, lat, lng, order, thresholds:{enabled,minSwellFt,minPeriodS,maxWindKn},
//     createdAt }
// Stored in Firestore so the spot list + alert thresholds persist and sync.

const ready = () => firebase.apps.length > 0 && db;
const COL = 'forecastSpots';
const API = process.env.REACT_APP_UPLOAD_API || '';
// True when forecastDb is a cross-project secondary app (nihirote → chadha).
const usesForecastWriteApi = () =>
  !!(
    process.env.REACT_APP_FORECAST_FIREBASE_PROJECT_ID &&
    process.env.REACT_APP_FORECAST_FIREBASE_PROJECT_ID !== process.env.REACT_APP_FIREBASE_PROJECT_ID &&
    API
  );

async function forecastApi(method, path, body) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to edit surf spots');
  const token = await user.getIdToken();
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.error ? `: ${j.error}` : '';
    } catch { /* ignore */ }
    throw new Error(`Spot save failed (${res.status}${detail})`);
  }
  return res.status === 204 ? null : res.json();
}

async function writeSpot(id, patch) {
  if (usesForecastWriteApi()) {
    await forecastApi('PATCH', `/forecast-spots/${encodeURIComponent(id)}`, patch);
    return;
  }
  await db.collection(COL).doc(id).set(patch, { merge: true });
}

// Initial spots to seed on first visit (slug ids keep them idempotent).
// `beachFacing` = compass bearing the beach looks out to sea (onshore wind comes
// FROM this direction); used to flag offshore/onshore wind. Approximate — editable.
export const DEFAULT_SPOTS = [
  { id: 'malibu-ca', name: 'Malibu', region: 'California, USA', lat: 34.0367, lng: -118.6779, beachFacing: 200 },
  { id: 'salina-cruz-mx', name: 'Salina Cruz', region: 'Oaxaca, Mexico', lat: 16.1667, lng: -95.1955, beachFacing: 180 },
  { id: 'pascuales-mx', name: 'Pascuales', region: 'Colima, Mexico', lat: 18.8492, lng: -103.7806, beachFacing: 210 },
  { id: 'puerto-escondido-mx', name: 'Puerto Escondido', region: 'Oaxaca, Mexico', lat: 15.8333, lng: -97.0667, beachFacing: 200 },
  { id: 'honolulu-hi', name: 'Honolulu', region: "O‘ahu (South Shore), Hawaii", lat: 21.2766, lng: -157.8451, beachFacing: 180 },
  { id: 'north-shore-oahu-hi', name: 'North Shore', region: "O‘ahu, Hawaii", lat: 21.6647, lng: -158.0525, beachFacing: 340 },
  { id: 'playa-colorado-ni', name: 'Playa Colorado', region: 'Tola, Nicaragua', lat: 11.4339, lng: -86.1906, beachFacing: 235 },
  { id: 'blacks-beach-ca', name: "Black's Beach", region: 'San Diego, USA', lat: 32.8896, lng: -117.2519, beachFacing: 260 },
  { id: 'uluwatu-id', name: 'Uluwatu', region: 'Bali, Indonesia', lat: -8.8147, lng: 115.0886, beachFacing: 225 },
  { id: 'cloudbreak-fj', name: 'Cloudbreak', region: 'Tavarua, Fiji', lat: -17.8527, lng: 177.1972, beachFacing: 225 },
  { id: 'teahupoo-pf', name: "Teahupo'o", region: 'Tahiti, French Polynesia', lat: -17.8489, lng: -149.2672, beachFacing: 200 },
  { id: 'cloud-9-ph', name: 'Cloud 9', region: 'Siargao, Philippines', lat: 9.8141, lng: 126.1700, beachFacing: 80 },
  { id: 'witches-rock-cr', name: 'Witches Rock', region: 'Guanacaste, Costa Rica', lat: 10.8861, lng: -85.8128, beachFacing: 270 },
  { id: 'bocas-del-toro-pa', name: 'Bocas del Toro', region: 'Panama', lat: 9.3400, lng: -82.2400, beachFacing: 30 },
  { id: 'nazare-pt', name: 'Nazaré', region: 'Portugal', lat: 39.6055, lng: -9.0855, beachFacing: 270 },
  { id: 'hossegor-fr', name: 'Hossegor', region: 'France', lat: 43.6630, lng: -1.4440, beachFacing: 290 },
  { id: 'lances-right-id', name: "Lance's Right", region: 'Mentawai Islands, Indonesia', lat: -2.0739, lng: 99.5683, beachFacing: 160 },
  // Rote (the /rote page lists every tracked spot whose region mentions Rote).
  // `tide` is the spot's working tide band; wind/swell-direction preferences
  // live in DEFAULT_STRIKES below (merged into spot.strike at read time).
  {
    id: 't-land-rote-id', name: 'T-Land (Nemberala)', region: 'Rote, Indonesia',
    lat: -10.8853, lng: 122.8127, beachFacing: 245,
    tide: { mode: 'custom', minStage: 0, maxStage: 0.65 }, // low → mid
  },
  {
    id: 'boa-rote-id', name: 'Bo’a', region: 'Rote, Indonesia',
    lat: -10.9284, lng: 122.8554, beachFacing: 190,
    tide: { mode: 'any' }, // works all tides (lower = hollower)
  },
  // ---- The rest of the Rote catalog (researched from surf guides; coords are
  // village/reef-pass level — the wave model is a 1/12° grid so that's plenty).
  {
    // Hollow right on the reef pass a few km N of Nemberala (aka Sakunamon /
    // "Peanuts" in some guides). Boat or long paddle; wind-affected.
    id: 'suckie-mamas-rote-id', name: 'Suckie Mamas', region: 'Rote, Indonesia',
    lat: -10.858, lng: 122.803, beachFacing: 250,
    tide: { mode: 'custom', minStage: 0.25, maxStage: 0.8 },
  },
  {
    // Playful lefts + rights over limestone reef at the E end of Nemberala
    // beach by the harbour — the beginner/intermediate wave.
    id: 'squealers-rote-id', name: 'Squealers', region: 'Rote, Indonesia',
    lat: -10.8925, lng: 122.8215, beachFacing: 245,
    tide: { mode: 'custom', minStage: 0.3, maxStage: 0.85 },
  },
  {
    // Fast hollow right off uninhabited Do'o island ~10 km W of Nemberala
    // (boat only). Breaks mid tide; wants light wind.
    id: 'doo-rote-id', name: 'Do’o', region: 'Rote, Indonesia',
    lat: -10.82, lng: 122.75, beachFacing: 220,
    tide: { mode: 'custom', minStage: 0.3, maxStage: 0.7 },
  },
  {
    // Reefs off Ndao island further W — experienced surfers, boat trips.
    id: 'ndao-rote-id', name: 'Ndao Island', region: 'Rote, Indonesia',
    lat: -10.835, lng: 122.655, beachFacing: 210,
    tide: { mode: 'any' },
  },
  {
    // Outer-reef bombie W of the T-Land reefs — holds serious size; the
    // big-swell strike option.
    id: 'bommie-rote-id', name: 'The Bommie', region: 'Rote, Indonesia',
    lat: -10.889, lng: 122.795, beachFacing: 240,
    tide: { mode: 'any' },
  },
  {
    // The inside Nemberala reef ("Bananas") — mellow learner wave in front of
    // the village (and the NIHI Rote / T-Land resort area).
    id: 'nemberala-beach-rote-id', name: 'Nemberala Beach (Bananas)', region: 'Rote, Indonesia',
    lat: -10.884, lng: 122.818, beachFacing: 245,
    tide: { mode: 'custom', minStage: 0.4, maxStage: 0.9 },
  },
  {
    // Forgiving reef in front of Dela village between Nemberala and Bo'a.
    id: 'dela-rote-id', name: 'Dela Beach', region: 'Rote, Indonesia',
    lat: -10.906, lng: 122.836, beachFacing: 235,
    tide: { mode: 'custom', minStage: 0.3, maxStage: 0.85 },
  },
  {
    // Point setup NW of Nemberala toward Sedeoen — intermediate wall.
    id: 'sedi-point-rote-id', name: 'Sedi Point', region: 'Rote, Indonesia',
    lat: -10.848, lng: 122.795, beachFacing: 255,
    tide: { mode: 'custom', minStage: 0.25, maxStage: 0.75 },
  },
  {
    // NE-coast option around the Landu Leko peninsula — the wet-season
    // (Nov–Mar) call when N/NW wind swell wraps the top of the island.
    id: 'landu-leko-rote-id', name: 'Landu Leko', region: 'Rote, Indonesia',
    lat: -10.62, lng: 123.38, beachFacing: 30,
    tide: { mode: 'any' },
  },
];

// Per-spot wave-height calibration anchors, researched from each break's known
// swell→face behavior. The model is faceFt = k · Hs^swellExp · (T/10)^periodExp,
// with k fit to the anchor session { faceFt observed, swellFt (deep-water Hs),
// periodS }. The exponents shape extrapolation: swellExp<1 = bigger swells
// magnify proportionally less; periodExp = how strongly longer period jacks the
// face (slabs/points high, short-fetch beachbreaks low).
//   - Open-Meteo reports deep-water significant swell height (Hs); these map it
//     to trough-to-crest FACE height (so thresholds read in face feet).
//   - Magnifying breaks (Pascuales, Puerto, Teahupo'o, Nazaré) carry a much
//     higher effective k; filtered/short-period spots (S-shore O‘ahu, Bocas) low.
// All are starting estimates — refine any spot by setting a real session anchor.
export const DEFAULT_CALIBRATIONS = {
  'malibu-ca': { swellExp: 0.9, periodExp: 0.6, anchor: { faceFt: 5, swellFt: 3.0, periodS: 15 } },
  'salina-cruz-mx': { swellExp: 0.8, periodExp: 0.6, anchor: { faceFt: 10, swellFt: 4.0, periodS: 16 } },
  'pascuales-mx': { swellExp: 0.8, periodExp: 0.5, anchor: { faceFt: 25, swellFt: 6.9, periodS: 16 } },
  'puerto-escondido-mx': { swellExp: 0.85, periodExp: 0.6, anchor: { faceFt: 15, swellFt: 5.0, periodS: 16 } },
  'honolulu-hi': { swellExp: 0.9, periodExp: 0.6, anchor: { faceFt: 4, swellFt: 2.3, periodS: 14 } },
  'north-shore-oahu-hi': { swellExp: 0.9, periodExp: 0.5, anchor: { faceFt: 12, swellFt: 6.0, periodS: 14 } },
  'playa-colorado-ni': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 7, swellFt: 3.5, periodS: 14 } },
  'blacks-beach-ca': { swellExp: 0.8, periodExp: 0.6, anchor: { faceFt: 9, swellFt: 4.0, periodS: 15 } },
  'uluwatu-id': { swellExp: 0.85, periodExp: 0.6, anchor: { faceFt: 8, swellFt: 3.5, periodS: 16 } },
  'cloudbreak-fj': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 12, swellFt: 5.0, periodS: 16 } },
  'teahupoo-pf': { swellExp: 0.85, periodExp: 0.7, anchor: { faceFt: 14, swellFt: 5.0, periodS: 16 } },
  'cloud-9-ph': { swellExp: 0.9, periodExp: 0.5, anchor: { faceFt: 7, swellFt: 4.0, periodS: 12 } },
  'witches-rock-cr': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 7, swellFt: 3.5, periodS: 14 } },
  'bocas-del-toro-pa': { swellExp: 0.9, periodExp: 0.5, anchor: { faceFt: 6, swellFt: 4.0, periodS: 9 } },
  'nazare-pt': { swellExp: 0.9, periodExp: 0.6, anchor: { faceFt: 40, swellFt: 8.0, periodS: 16 } },
  'hossegor-fr': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 10, swellFt: 5.0, periodS: 14 } },
  'lances-right-id': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 8, swellFt: 4.0, periodS: 15 } },
  // T-Land holds size but is a rippable wall, not a magnifier: 4ft @ 16s SW ≈ head-high+.
  't-land-rote-id': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 8, swellFt: 4.0, periodS: 16 } },
  // Bo'a jacks on the shallow reef — a modest long-period swell throws well-overhead slabs.
  'boa-rote-id': { swellExp: 0.85, periodExp: 0.6, anchor: { faceFt: 8, swellFt: 3.5, periodS: 16 } },
  // Suckies needs a bit of swell to break far out on the pass, then hollows fast.
  'suckie-mamas-rote-id': { swellExp: 0.85, periodExp: 0.6, anchor: { faceFt: 7, swellFt: 3.5, periodS: 15 } },
  // Squealers filters the raw swell — stays a friendly size on most days.
  'squealers-rote-id': { swellExp: 0.9, periodExp: 0.5, anchor: { faceFt: 4, swellFt: 3.0, periodS: 14 } },
  // Do'o catches the channel swell head-on and throws over shallow coral.
  'doo-rote-id': { swellExp: 0.85, periodExp: 0.6, anchor: { faceFt: 8, swellFt: 3.5, periodS: 16 } },
  'ndao-rote-id': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 8, swellFt: 4.0, periodS: 16 } },
  // The Bommie is the magnifier — long-period lines stand up well overhead out there.
  'bommie-rote-id': { swellExp: 0.85, periodExp: 0.65, anchor: { faceFt: 12, swellFt: 5.0, periodS: 16 } },
  // Inside Nemberala is heavily filtered by the outer reefs.
  'nemberala-beach-rote-id': { swellExp: 0.9, periodExp: 0.45, anchor: { faceFt: 3.5, swellFt: 3.0, periodS: 14 } },
  'dela-rote-id': { swellExp: 0.9, periodExp: 0.5, anchor: { faceFt: 4, swellFt: 3.0, periodS: 14 } },
  'sedi-point-rote-id': { swellExp: 0.85, periodExp: 0.55, anchor: { faceFt: 6, swellFt: 3.5, periodS: 15 } },
  // North coast only sees wrapped/short-period energy — modest faces.
  'landu-leko-rote-id': { swellExp: 0.9, periodExp: 0.5, anchor: { faceFt: 4, swellFt: 3.0, periodS: 12 } },
};

// Per-spot strike-bar overrides. Most spots auto-scale their "epic" bar from the
// calibration anchor (~0.7×), but some anchors are an all-time session rather
// than a sensible strike threshold, so we pin the face (and optionally period)
// here. These are still just defaults — a spot's saved ⚙ strike settings win.
export const DEFAULT_STRIKES = {
  'pascuales-mx': { minSwellFt: 12 },
  // Rote spot preferences (researched; refine from sessions):
  //   T-Land — SW swell window, E–SE trades offshore all season.
  't-land-rote-id': { swellDirFrom: 185, swellDirTo: 255, windDirFrom: 60, windDirTo: 160 },
  //   Bo'a — S–SW swell; needs N–NW (or no) wind: the SE trades blow it out.
  'boa-rote-id': { swellDirFrom: 170, swellDirTo: 240, windDirFrom: 290, windDirTo: 30, maxWindKn: 12 },
  //   Suckies — same SW window as T-Land but wind-affected: keep it light.
  'suckie-mamas-rote-id': { swellDirFrom: 185, swellDirTo: 255, windDirFrom: 60, windDirTo: 160, maxWindKn: 10 },
  //   Squealers — forgiving; takes the trades better than anywhere else.
  'squealers-rote-id': { swellDirFrom: 185, swellDirTo: 255, windDirFrom: 60, windDirTo: 160, maxWindKn: 14 },
  //   Do'o — wants light/variable or W–N flow (onshore at Nemberala = clean out there).
  'doo-rote-id': { swellDirFrom: 185, swellDirTo: 250, windDirFrom: 250, windDirTo: 20, maxWindKn: 8 },
  'ndao-rote-id': { swellDirFrom: 180, swellDirTo: 250, maxWindKn: 10 },
  //   Bommie — only a real mission on proper long-period SW swell.
  'bommie-rote-id': { swellDirFrom: 185, swellDirTo: 255, windDirFrom: 60, windDirTo: 160, maxWindKn: 12, minSwellFt: 10, minPeriodS: 14 },
  'nemberala-beach-rote-id': { swellDirFrom: 185, swellDirTo: 255, windDirFrom: 60, windDirTo: 160, maxWindKn: 16 },
  'dela-rote-id': { swellDirFrom: 180, swellDirTo: 250, windDirFrom: 60, windDirTo: 160, maxWindKn: 14 },
  'sedi-point-rote-id': { swellDirFrom: 190, swellDirTo: 260, windDirFrom: 60, windDirTo: 160, maxWindKn: 12 },
  //   Landu Leko — wet-season N/NW windswell; S-quadrant land breeze is offshore.
  'landu-leko-rote-id': { swellDirFrom: 320, swellDirTo: 60, windDirFrom: 150, windDirTo: 250, maxWindKn: 12 },
};

// Live surf cams per spot — researched Aug 2026 (see brain/log). Each cam:
//   { type: 'still'|'youtube'|'embed'|'link', url, title, attribution?, link? }
//   - still:   a snapshot JPEG refreshed every few minutes (Surfline's open
//              camstill CDN — free, no auth; the page re-fetches on a timer).
//   - youtube: a public YouTube live stream, embedded.
//   - embed:   an arbitrary iframe URL.
//   - link:    a link-out card to a cam we can't embed (permission/player).
// Stored on the spot doc (`cams`) so it's admin-editable with zero deploys;
// these are just the seeded defaults for spots where a cam exists.
const SL = 'https://camstills.cdn-surfline.com/us-west-2';
export const DEFAULT_CAMS = {
  'malibu-ca': [
    { type: 'still', title: 'First Point', url: `${SL}/wc-malibu/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com' },
    { type: 'still', title: 'First Point close-up', url: `${SL}/wc-malibuclose/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com' },
  ],
  'puerto-escondido-mx': [
    { type: 'still', title: 'Zicatela', url: `${SL}/mx-puerto/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com/surf-report/puerto-escondido/5842041f4e65fad6a7708b43' },
    { type: 'link', title: 'Playa Zicatela live stream', url: 'https://www.skylinewebcams.com/en/webcam/mexico/oaxaca/puerto-escondido/playa-zicatela.html', attribution: 'SkylineWebcams' },
  ],
  'honolulu-hi': [
    { type: 'youtube', title: 'South Shore surf cam', url: 'https://www.youtube.com/watch?v=WIUg8wzhq1g', attribution: 'Waikiki Aquarium' },
    { type: 'still', title: 'Queens', url: `${SL}/hi-queens/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com' },
    { type: 'still', title: 'Waikiki Beach', url: `${SL}/hi-waikikibeach/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com' },
  ],
  'north-shore-oahu-hi': [
    { type: 'still', title: 'Pipeline', url: `${SL}/hi-pipeline/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com' },
    { type: 'youtube', title: 'Waimea Bay live', url: 'https://www.youtube.com/watch?v=Vl-IBa9JTH4', attribution: 'afarTV' },
  ],
  'teahupoo-pf': [
    { type: 'still', title: 'Lineup', url: `${SL}/pf-teahupoo/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com/surf-report/teahupo-o/5842041f4e65fad6a7708dbf' },
    { type: 'still', title: 'Channel', url: `${SL}/pf-teahupoochannel/latest_full.jpg`, attribution: 'Surfline', link: 'https://www.surfline.com/surf-report/teahupo-o/5842041f4e65fad6a7708dbf' },
  ],
  'uluwatu-id': [
    { type: 'link', title: 'Uluwatu live cam', url: 'https://www.pemburuombak.com/live-webcams/bali/uluwatu-live', attribution: 'PemburuOmbak' },
    { type: 'link', title: 'Uluwatu cam (alt)', url: 'https://balicams.com/camera/uluwatu', attribution: 'Balicams' },
  ],
  'lances-right-id': [
    { type: 'link', title: "Lance's Right live cam", url: 'https://www.pemburuombak.com/live-webcams/sumatra/sumatra-mentawai-lances-right', attribution: 'PemburuOmbak' },
  ],
  't-land-rote-id': [
    { type: 'link', title: 'Nembrala live cam', url: 'https://www.pemburuombak.com/live-webcams/rote/rote-nembrala', attribution: 'PemburuOmbak' },
  ],
  'nemberala-beach-rote-id': [
    { type: 'link', title: 'Nembrala live cam', url: 'https://www.pemburuombak.com/live-webcams/rote/rote-nembrala', attribution: 'PemburuOmbak' },
  ],
  'boa-rote-id': [
    { type: 'link', title: 'Nembrala live cam (nearby)', url: 'https://www.pemburuombak.com/live-webcams/rote/rote-nembrala', attribution: 'PemburuOmbak' },
  ],
  'witches-rock-cr': [
    { type: 'link', title: 'Tamarindo cams (nearby)', url: 'https://www.witchsrocksurfcamp.com/tamarindo-live-surf-cams/', attribution: 'Witch’s Rock Surf Camp' },
  ],
};

export function useForecastSpots() {
  const [spots, setSpots] = useState(null);
  useEffect(() => {
    if (!ready()) {
      setSpots([]);
      return undefined;
    }
    const unsub = db.collection(COL).onSnapshot(
      (snap) => {
        let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Rote spots surface only on sites that carry the Rote pages —
        // the shared catalog itself is unchanged (produced by chadha).
        // SITE.spotAllow lists per-site exceptions (e.g. Bo'a on high.surf).
        if (!SITE.hasRote) {
          const allow = new Set(SITE.spotAllow || []);
          items = items.filter((s) => allow.has(s.id) || !/rote/i.test(s.region || ''));
        }
        items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name || '').localeCompare(b.name || ''));
        setSpots(items);
      },
      () => setSpots([])
    );
    return () => unsub();
  }, []);
  return spots;
}

// Seed the default spots once, if the collection is empty.
// Producer-site only — satellite browsers can't write the shared catalog.
export async function seedDefaultSpots() {
  if (!ready() || usesForecastWriteApi()) return;
  const snap = await db.collection(COL).limit(1).get();
  if (!snap.empty) return;
  const batch = db.batch();
  DEFAULT_SPOTS.forEach((s, i) => {
    batch.set(db.collection(COL).doc(s.id), {
      name: s.name,
      region: s.region,
      lat: s.lat,
      lng: s.lng,
      beachFacing: s.beachFacing ?? null,
      order: i,
      thresholds: { ...DEFAULT_THRESHOLDS },
      ...(s.tide ? { tide: s.tide } : {}),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  });
  await batch.commit();
}

// Idempotently add any default spots that aren't already in the collection (by
// id) — so newly shipped default spots get created for existing users without
// re-adding ones they've intentionally removed. `existing` is the current list.
export async function ensureDefaultSpots(existing) {
  if (!ready() || !Array.isArray(existing) || usesForecastWriteApi()) return;
  // CRITICAL: check the REAL collection, not the passed-in UI list — that list
  // is site-filtered (Rote spots hidden off-nihirote), and trusting it once
  // caused an infinite loop that re-created "missing" hidden spots on every
  // snapshot, clobbering their tuned strike/calibration fields.
  const snap = await db.collection(COL).get();
  const have = new Set(snap.docs.map((d) => d.id));
  const missing = DEFAULT_SPOTS.filter((s) => !have.has(s.id));
  if (!missing.length) return;
  const baseOrder = snap.docs.reduce((m, d) => Math.max(m, d.data().order ?? 0), 0) + 1;
  const batch = db.batch();
  missing.forEach((s, i) => {
    batch.set(db.collection(COL).doc(s.id), {
      name: s.name,
      region: s.region,
      lat: s.lat,
      lng: s.lng,
      beachFacing: s.beachFacing ?? null,
      order: baseOrder + i,
      thresholds: { ...DEFAULT_THRESHOLDS },
      ...(s.tide ? { tide: s.tide } : {}),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  });
  await batch.commit();
}

// Backfill researched wave-height calibrations onto tracked spots that don't yet
// have one. Non-destructive: any spot the user has already calibrated (has a
// `calibration` field) is left untouched, so manual tuning is never clobbered.
export async function ensureSpotCalibrations(existing) {
  if (!ready() || !Array.isArray(existing) || usesForecastWriteApi()) return;
  const batch = db.batch();
  let n = 0;
  existing.forEach((s) => {
    const def = DEFAULT_CALIBRATIONS[s.id];
    if (!def || s.calibration) return;
    batch.set(
      db.collection(COL).doc(s.id),
      { calibration: { enabled: true, periodRef: 10, ...def } },
      { merge: true }
    );
    n += 1;
  });
  if (n) await batch.commit();
}

// Backfill researched live cams onto spots that don't have any yet.
// Non-destructive: a spot with a `cams` field (even an emptied one) is left
// alone, so an admin who removed a dead cam never sees it resurrected.
export async function ensureSpotCams(existing) {
  if (!ready() || !Array.isArray(existing) || usesForecastWriteApi()) return;
  const batch = db.batch();
  let n = 0;
  existing.forEach((s) => {
    const def = DEFAULT_CAMS[s.id];
    if (!def || s.cams !== undefined) return;
    batch.set(db.collection(COL).doc(s.id), { cams: def }, { merge: true });
    n += 1;
  });
  if (n) await batch.commit();
}

// Per-spot live cams: array of { type, url, title, attribution?, link? }.
export async function setSpotCams(id, cams) {
  if (!ready() || !id) return;
  await writeSpot(id, { cams });
}

export async function addSpot({ name, region, lat, lng, beachFacing }, order = 999) {
  if (!ready()) return null;
  const payload = {
    name: name || 'New spot',
    region: region || '',
    lat: Number(lat),
    lng: Number(lng),
    beachFacing: Number.isFinite(Number(beachFacing)) ? Number(beachFacing) : null,
    order,
    thresholds: { ...DEFAULT_THRESHOLDS },
  };
  if (usesForecastWriteApi()) {
    const created = await forecastApi('POST', '/forecast-spots', payload);
    return created?.id || null;
  }
  const ref = db.collection(COL).doc();
  await ref.set({
    ...payload,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  });
  return ref.id;
}

export async function updateSpot(id, patch) {
  if (!ready() || !id) return;
  await writeSpot(id, patch);
}

export async function setSpotThresholds(id, thresholds) {
  if (!ready() || !id) return;
  await writeSpot(id, { thresholds });
}

// Strike-mission thresholds: { enabled, minSwellFt, minPeriodS, maxWindKn,
// windDirFrom, windDirTo }. Stored alongside the everyday alert thresholds.
export async function setSpotStrike(id, strike) {
  if (!ready() || !id) return;
  await writeSpot(id, { strike });
}

// Per-spot wave-height calibration: { enabled, gain, periodRef, periodExp } —
// maps deep-water swell height to the breaking/face height the spot produces.
export async function setSpotCalibration(id, calibration) {
  if (!ready() || !id) return;
  await writeSpot(id, { calibration });
}

// Per-spot tide preference: { mode: 'any'|'low'|'mid'|'high'|'custom',
// minStage, maxStage } — gates strike/alert windows to the tide the spot works.
export async function setSpotTide(id, tide) {
  if (!ready() || !id) return;
  await writeSpot(id, { tide });
}

export async function removeSpot(id) {
  if (!ready() || !id) return;
  if (usesForecastWriteApi()) {
    await forecastApi('DELETE', `/forecast-spots/${encodeURIComponent(id)}`);
    return;
  }
  await db.collection(COL).doc(id).delete();
}

// Max age of a backend-cached forecast before we prefer a fresh live fetch.
const CACHE_MAX_AGE_MS = 18 * 60 * 60 * 1000;

// Load a spot's forecast: prefer the backend-cached doc (Stormglass best-blend
// swell/wind, refreshed every ~6h — no per-load rate limits), and fall back to
// the live in-browser Open-Meteo path when the cache is missing/stale.
export async function loadSpotForecast(spot, days = 16) {
  if (spot?.id && ready()) {
    try {
      const snap = await db.collection('forecastCache').doc(spot.id).get();
      if (snap.exists) {
        const data = snap.data();
        const age = Date.now() - (data.fetchedAt || 0);
        if (data.status === 'ready' && data.hours?.length && age < CACHE_MAX_AGE_MS) {
          return prepareCachedForecast(data);
        }
      }
    } catch {
      // fall through to live fetch
    }
  }
  return fetchSpotForecast(spot.lat, spot.lng, days, spot.beachFacing);
}

// Cache-only read of a spot's cached forecast (no live Open-Meteo fallback) —
// used to prefetch present conditions for many spots cheaply without risking a
// rate-limited fan-out of live API calls. Returns null on miss/stale/error.
export async function loadSpotForecastCached(spot) {
  if (!spot?.id || !ready()) return null;
  try {
    const snap = await db.collection('forecastCache').doc(spot.id).get();
    if (!snap.exists) return null;
    const data = snap.data();
    const age = Date.now() - (data.fetchedAt || 0);
    if (data.status === 'ready' && data.hours?.length && age < CACHE_MAX_AGE_MS) {
      return prepareCachedForecast(data);
    }
  } catch {
    /* ignore */
  }
  return null;
}
